// Copyright (c) Ping. All rights reserved.

import type React from 'react';
import { useCallback, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  type ListRenderItem,
  Pressable,
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { createTheme } from '../../design-system/theme';
import { ActivityEmptyState } from './ActivityEmptyState';
import { ActivityFiltersBar } from './ActivityFiltersBar';
import { ActivityItem as ActivityRow } from './ActivityItem';
import { ActivitySortSheet } from './ActivitySortSheet';
import { activityStrings } from './strings';
import type {
  ActivityItem as ActivityItemRecord,
  ActivityLoader,
  ActivitySubscriber,
} from './types';
import { useActivityFeedViewModel } from './useActivityFeedViewModel';

export interface ActivityFeedScreenProps {
  readonly load: ActivityLoader;
  readonly subscribe: ActivitySubscriber;
  readonly onItemPress?: ((item: ActivityItemRecord) => void) | undefined;
}

const ROW_HEIGHT_ESTIMATE = 76;

/** Top-level activity feed: filter bar, list, sort sheet, empty / error states. */
export const ActivityFeedScreen: React.FC<ActivityFeedScreenProps> = ({
  load,
  subscribe,
  onItemPress,
}) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');
  const vm = useActivityFeedViewModel({ load, subscribe });
  const [sortVisible, setSortVisible] = useState(false);

  const keyExtractor = useCallback((item: ActivityItemRecord) => item.id, []);

  const renderItem: ListRenderItem<ActivityItemRecord> = useCallback(
    ({ item }) => <ActivityRow item={item} onPress={onItemPress} />,
    [onItemPress],
  );

  const getItemLayout = useCallback(
    (_: ArrayLike<ActivityItemRecord> | null | undefined, index: number) => ({
      length: ROW_HEIGHT_ESTIMATE,
      offset: ROW_HEIGHT_ESTIMATE * index,
      index,
    }),
    [],
  );

  const handleOpenSort = useCallback(() => setSortVisible(true), []);
  const handleCloseSort = useCallback(() => setSortVisible(false), []);

  const renderBody = (): React.ReactElement => {
    if (vm.state.kind === 'loading') {
      return (
        <View style={styles.center}>
          <ActivityIndicator size="small" color={theme.colors.accent.indigo} />
        </View>
      );
    }
    if (vm.state.kind === 'error') {
      return (
        <View style={styles.center}>
          <Text style={[styles.errorTitle, { color: theme.colors.text.primary }]}>
            {activityStrings.errorTitle}
          </Text>
          <Pressable
            onPress={vm.retry}
            accessibilityRole="button"
            accessibilityLabel={activityStrings.errorRetry}
            style={({ pressed }) => [
              styles.retryButton,
              { backgroundColor: theme.colors.accent.indigo },
              pressed && { opacity: 0.85 },
            ]}
          >
            <Text style={styles.retryText}>{activityStrings.errorRetry}</Text>
          </Pressable>
        </View>
      );
    }
    if (vm.state.kind === 'empty') {
      return <ActivityEmptyState filter={vm.state.filter} />;
    }

    return (
      <FlatList<ActivityItemRecord>
        data={vm.state.items}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        getItemLayout={getItemLayout}
        windowSize={10}
        initialNumToRender={15}
        maxToRenderPerBatch={10}
        removeClippedSubviews={false}
      />
    );
  };

  return (
    <View
      style={[styles.container, { backgroundColor: theme.colors.surface.primary }]}
      accessibilityLabel={activityStrings.screenTitle}
    >
      <View style={[styles.header, { borderBottomColor: theme.colors.border.secondary }]}>
        <Text style={[styles.title, { color: theme.colors.text.primary }]}>
          {activityStrings.screenTitle}
        </Text>
        <Pressable
          onPress={handleOpenSort}
          accessibilityRole="button"
          accessibilityLabel={activityStrings.sortLabel}
          style={({ pressed }) => [pressed && { opacity: 0.6 }]}
        >
          <Text style={[styles.sortAction, { color: theme.colors.accent.indigo }]}>
            {activityStrings.sort(vm.sort)}
          </Text>
        </Pressable>
      </View>
      <ActivityFiltersBar active={vm.filter} onChange={vm.setFilter} />
      {renderBody()}
      <ActivitySortSheet
        visible={sortVisible}
        active={vm.sort}
        onSelect={vm.setSort}
        onClose={handleCloseSort}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  } satisfies ViewStyle,
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
  } satisfies ViewStyle,
  title: {
    fontSize: 17,
    fontWeight: '600',
  } satisfies TextStyle,
  sortAction: {
    fontSize: 14,
    fontWeight: '600',
  } satisfies TextStyle,
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  } satisfies ViewStyle,
  errorTitle: {
    fontSize: 17,
    fontWeight: '600',
    marginBottom: 12,
    textAlign: 'center',
  } satisfies TextStyle,
  retryButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
  } satisfies ViewStyle,
  retryText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  } satisfies TextStyle,
});
